import { Colors } from "@/enums/Colors";
import UserRole from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/UserRole";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import LazoRoleService from "@/services/LazoRoleService";
import LazoTeamService from "@/services/LazoTeamService";
import LazoUserService from "@/services/LazoUserService";
import { LazoArea } from "@/types/LazoArea";
import { LazoJobTitle } from "@/types/LazoJobTitle";
import { ASSIGNABLE_LAZO_PORTAL_ROLES, mapRolesToString } from "@/types/LazoUserRoles";
import UserCreation from "@/types/UserCreation";
import { AddOutlined, ExpandMore } from "@mui/icons-material";
import { Box, FormLabel, MenuItem, TextField, Typography, outlinedInputClasses, selectClasses, styled } from "@mui/material";
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { InformationRowContainer } from "../lazo-one-settings-components/profile-components/CompanyInformation";
import { InformationFormContainer } from "../lazo-one-settings-components/profile-components/PersonalInformationEditForm";
import CommonButton from "../utils/CommonButton";
import CommonFormControl from "../utils/CommonFormControl";
import LazoModal from "../utils/LazoModal";
import { CommonStyledSelect, CommonStyledSelectMenuProps } from "../utils/SelectStyles";

interface Props {
  onCreateUser?: () => void;
}

function CustomSelect({ role, setRole, roles }: { role: string; setRole: Dispatch<SetStateAction<string>>; roles: UserRole[] }) {
  return (
    <StyledSelect
      value={role}
      onChange={(e) => setRole(e.target.value as string)}
      IconComponent={ExpandMore}
      MenuProps={CommonStyledSelectMenuProps}
    >
      {roles.map((r) => (
        <MenuItem key={r.externalId} value={r.externalId!}>
          <Typography>{mapRolesToString(r.name)}</Typography>
        </MenuItem>
      ))}
    </StyledSelect>
  );
}

export default function CreateLazoTeamMemberModal(props: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [role, setRole] = useState<string>("");
  const [lazoOneRole, setLazoOneRole] = useState<string>("");

  const [allowLazoOne, setAllowLazoOne] = useState<boolean>(false);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [lazoOneRoles, setLazoOneRoles] = useState<UserRole[]>([]);
  const [areas, setAreas] = useState<LazoArea[]>([]);
  const [availableJobTitles, setAvailableJobTitles] = useState<LazoJobTitle[]>([]);

  useEffect(() => {
    LazoRoleService.getAllPortalRoles().then((r) => {
      r.onSuccess((content) => {
        const filtered = content.filter((role) => ASSIGNABLE_LAZO_PORTAL_ROLES.includes(role.name));
        setRoles(filtered);
        if (filtered[0]) {
          setRole(filtered[0].externalId!);
        }
      });
      r.onError((e) => console.log("There was an error getting the roles. Message: ", e));
    });
    LazoTeamService.getAllAreas().then((r) => {
      r.onSuccess((data) => setAreas(data));
    });
  }, []);

  function handleAllowAccessToLazoOneCheck(e: ChangeEvent<HTMLInputElement>) {
    setAllowLazoOne(e.currentTarget.checked);
    if (lazoOneRoles.length === 0 && e.currentTarget.checked) {
      LazoRoleService.getAllCompanyRoles().then((r) => {
        r.onSuccess((roles) => {
          setLazoOneRoles(roles);
          setLazoOneRole(roles.find((r) => r.name.includes("viewer")).externalId);
        });
        r.onError((e) => NotificationService.error("There was an error obtaining Lazo One roles. Error: " + e));
      });
    } else if (lazoOneRoles.length > 0 && e.currentTarget.checked) {
      setLazoOneRole(lazoOneRoles.find((r) => r.name.includes("viewer")).externalId);
    } else if (!e.currentTarget.checked) {
      setLazoOneRole("");
    }
  }

  function registerUser(data: {
    area: string;
    jobTitle: string;
    firstName: string;
    lastName: string;
    email: string;
    lazoPortalRole: string;
    password: string;
    otherJobTitle: string;
  }) {
    const selectedJobTitle = availableJobTitles.find((jt) => jt.externalId === data.jobTitle);
    const selectedArea = areas.find((a) => a.externalId === data.area);
    let userCreation: UserCreation = {
      title: data.jobTitle === "Other" ? data.otherJobTitle : selectedJobTitle?.name || "",
      area: selectedArea?.name || "",
      jobTitleExternalId: data.jobTitle === "Other" ? "" : data.jobTitle,
      hasAcceptedTerms: true,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      password: data.password,
      roleExternalId: data.lazoPortalRole,
      lazoOneRoleExternalId: lazoOneRole,
    };
    setIsSubmitting(true);
    LazoUserService.createUser(userCreation)
      .then((res) => {
        res.onSuccess(() => {
          props.onCreateUser();
          setIsOpen(false);
          reset();
          setLazoOneRole("");
          NotificationService.info("User created succesffully");
        });
        res.onError((e) => {
          NotificationService.error(e);
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  const { register, handleSubmit, control, reset, watch, setValue } = useForm();

  return (
    <>
      <CreateUserButton endIcon={<AddOutlined />} onClick={() => setIsOpen(true)}>
        <Typography fontWeight={600}>Create User</Typography>
      </CreateUserButton>

      <LazoModal
        open={isOpen}
        handleClose={() => setIsOpen(false)}
        disableClose={isSubmitting}
        title="Create new Lazo Team member"
        sx={{
          width: "450px",
          "& .content-container": {
            height: "27.25rem !important",
            overflowY: "auto",
            "& > div": {
              height: "inherit",
            },
          },
        }}
        buttons={
          <>
            <CommonButton purpleBorderVariant onClick={() => setIsOpen(false)} disabled={isSubmitting}>
              Cancel
            </CommonButton>
            <CommonButton onClick={handleSubmit(registerUser)} loading={isSubmitting}>
              Create
            </CommonButton>
          </>
        }
      >
        <>
          <InformationFormContainer>
            <InformationRowContainer gap={"inherit"}>
              <CommonFormControl sx={{ width: "50%" }}>
                <FormLabel>First Name</FormLabel>
                <Controller
                  control={control}
                  name={"firstName"}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      {...register("firstName", { required: true })}
                      defaultValue={value}
                      placeholder={error && "First name required"}
                      onChange={onChange}
                      error={!!error}
                      value={value}
                    />
                  )}
                />
              </CommonFormControl>
              <CommonFormControl sx={{ width: "50%" }}>
                <FormLabel>Last Name</FormLabel>
                <Controller
                  control={control}
                  name={"lastName"}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      {...register("lastName", { required: true })}
                      defaultValue={value}
                      placeholder={error && "Last name required"}
                      onChange={onChange}
                      error={!!error}
                      value={value}
                    />
                  )}
                />
              </CommonFormControl>
            </InformationRowContainer>
            <CommonFormControl>
              <Controller
                control={control}
                name={"area"}
                render={({ field: { value, onChange: onAreaChange }, fieldState: { error } }) => (
                  <>
                    <FormLabel>
                      Area
                      {error && <span style={{ color: "rgb(220 126 126)" }}>- Area required</span>}
                    </FormLabel>
                    <CommonStyledSelect
                      {...register("area", { required: true })}
                      IconComponent={ExpandMore}
                      placeholder={error && "Area required"}
                      MenuProps={CommonStyledSelectMenuProps}
                      error={!!error}
                      value={value}
                      onChange={(e) => {
                        onAreaChange(e);
                        const areaExternalId = e.target.value as string;
                        LazoTeamService.getJobTitles(areaExternalId).then((r) => {
                          r.onSuccess((data) => setAvailableJobTitles(data));
                        });
                        setValue("jobTitle", "");
                        setValue("otherJobTitle", "");
                      }}
                    >
                      {areas.map((area) => (
                        <MenuItem key={area.externalId} value={area.externalId}>
                          {area.name}
                        </MenuItem>
                      ))}
                    </CommonStyledSelect>
                  </>
                )}
              />
            </CommonFormControl>

            <CommonFormControl>
              <Controller
                control={control}
                name={"jobTitle"}
                render={({ field: { value }, fieldState: { error } }) => {
                  const selectedArea = watch("area");
                  return (
                    <>
                      <FormLabel>
                        Job Title
                        {error && <span style={{ color: "rgb(220 126 126)" }}>- Job title required</span>}
                      </FormLabel>
                      <CommonStyledSelect
                        {...register("jobTitle", { required: true })}
                        IconComponent={ExpandMore}
                        placeholder={error && "Job title required"}
                        MenuProps={CommonStyledSelectMenuProps}
                        error={!!error}
                        value={value}
                        disabled={!selectedArea}
                      >
                        {availableJobTitles.map((jt) => (
                          <MenuItem key={jt.externalId} value={jt.externalId}>
                            {jt.name}
                          </MenuItem>
                        ))}
                        <MenuItem key="other" value="Other">
                          Other
                        </MenuItem>
                      </CommonStyledSelect>

                      {value && value === "Other" && (
                        <Controller
                          control={control}
                          name={"otherJobTitle"}
                          render={({ field: { onChange, value: otherJobtitleValue }, fieldState: { error: otherJobtitleError } }) => (
                            <>
                              <FormLabel sx={{ paddingTop: "1rem" }}>Add your Job title </FormLabel>
                              <TextField
                                {...register("otherJobTitle", {
                                  required: true,
                                })}
                                placeholder={otherJobtitleError && "Job title is required"}
                                onChange={onChange}
                                error={!!otherJobtitleError}
                                value={otherJobtitleValue}
                              />
                            </>
                          )}
                        />
                      )}
                    </>
                  );
                }}
              />
            </CommonFormControl>

            <CommonFormControl>
              <FormLabel>Lazo Portal Role</FormLabel>
              <Controller
                control={control}
                name={"lazoPortalRole"}
                defaultValue={role}
                render={({ field: { value } }) => (
                  <CommonStyledSelect
                    {...register("lazoPortalRole")}
                    IconComponent={ExpandMore}
                    value={value}
                    MenuProps={CommonStyledSelectMenuProps}
                  >
                    {roles.map((r) => (
                      <MenuItem key={r.externalId} value={r.externalId!}>
                        <Typography>{mapRolesToString(r.name)}</Typography>
                      </MenuItem>
                    ))}
                  </CommonStyledSelect>
                )}
              />
            </CommonFormControl>
            {/* <Container className="lazo-one-role-container">
              <Container className="checkbox-container">
                <Typography>Allow access to Lazo One?</Typography>
                <LazoCheckbox
                  checked={allowLazoOne}
                  onChange={(e) => {
                    handleAllowAccessToLazoOneCheck(e);
                  }}
                  className="lazo-one-role-checkbox"
                />
              </Container>
              {allowLazoOne && lazoOneRoles.length > 0 && <CustomSelect role={lazoOneRole} setRole={setLazoOneRole} roles={lazoOneRoles} />}
            </Container> */}

            <CommonFormControl>
              <FormLabel>Email</FormLabel>
              <Controller
                control={control}
                name={"email"}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextField
                    {...register("email", { required: true })}
                    defaultValue={value}
                    placeholder={error && "Email required"}
                    onChange={onChange}
                    error={!!error}
                    value={value}
                  />
                )}
              />
            </CommonFormControl>
            <CommonFormControl>
              <FormLabel>Passsword</FormLabel>
              <Controller
                control={control}
                name={"password"}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextField
                    {...register("password", { required: true })}
                    defaultValue={value}
                    placeholder={error && "Password required"}
                    onChange={onChange}
                    error={!!error}
                    autoComplete="off"
                    value={value}
                  />
                )}
              />
            </CommonFormControl>
          </InformationFormContainer>
        </>
      </LazoModal>
    </>
  );
}

const StyledSelect = styled(CommonStyledSelect)(() => ({
  height: "2.000rem",
  color: `${Colors.BLACK}`,
  [`& .${outlinedInputClasses.notchedOutline}`]: {
    border: 0,
  },
  [`& .${selectClasses.select}`]: {
    [`& .${selectClasses.outlined}`]: {
      paddingRight: "1.500rem",
    },
  },
}));

const CreateUserButton = styled(CommonButton)(() => ({
  height: "2.5rem",
  whiteSpace: "nowrap",
  paddingInline: "16px",
  borderRadius: "0.5rem",
}));

const Container = styled(Box)(() => ({
  "&.lazo-one-role-container": {
    paddingLeft: "10px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    "& .checkbox-container": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      columnGap: "0.9375rem",
    },
    "& .lazo-one-role-checkbox": {
      width: "1rem",
      height: "1rem",
      "&.MuiCheckbox-root": {
        color: `${Colors.LIGHT_GRAY_DEDEE1}`,
        "& .MuiSvgIcon-root": {
          fontSize: "1.15rem",
        },
      },
    },
  },
}));
